/**
 * useRoamingConfig — read/write settings via Office.context.roamingSettings.
 * Settings follow the user across devices.
 */
import { useState, useEffect, useCallback } from "react";
import { DEFAULT_CONFIG } from "../../shared/constants";

/* global Office */

const SETTINGS_KEY = "ianAssistConfig";

export function useRoamingConfig() {
  const [config, setConfig] = useState(DEFAULT_CONFIG);

  // Load on mount
  useEffect(() => {
    try {
      const saved = Office.context.roamingSettings.get(SETTINGS_KEY);
      if (saved && typeof saved === "object") {
        // n8nUrl is build-time derived (includes proxy path in dev).
        // Never restore it from saved settings — always use the current build default.
        const { n8nUrl: _discarded, ...rest } = saved;
        console.log('[useRoamingConfig] loaded saved config, keys:', Object.keys(rest));
        console.log('[useRoamingConfig] using build-time n8nUrl:', DEFAULT_CONFIG.n8nUrl);
        setConfig({ ...DEFAULT_CONFIG, ...rest });
      } else {
        console.log('[useRoamingConfig] no saved config, using defaults');
        console.log('[useRoamingConfig] n8nUrl:', DEFAULT_CONFIG.n8nUrl);
      }
    } catch (err) {
      console.warn("[useRoamingConfig] Failed to load roaming config:", err);
    }
  }, []);

  // Update a partial config
  const updateConfig = useCallback(
    (partial) => {
      setConfig((prev) => {
        const next = { ...prev, ...partial };
        try {
          Office.context.roamingSettings.set(SETTINGS_KEY, next);
          Office.context.roamingSettings.saveAsync((result) => {
            if (result.status !== Office.AsyncResultStatus.Succeeded) {
              console.warn("Failed to save roaming settings:", result.error);
            }
          });
        } catch (err) {
          console.warn("roamingSettings not available:", err);
        }
        return next;
      });
    },
    []
  );

  // Reset to defaults
  const resetConfig = useCallback(() => {
    updateConfig(DEFAULT_CONFIG);
  }, [updateConfig]);

  return [config, updateConfig, resetConfig];
}

export default useRoamingConfig;
